import { useCallback, useMemo } from 'react'
import { CircularProgress, useTheme } from '@mui/material'
import { useSelector } from 'react-redux'
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom'
import { Box, Button, Container, Stack, Typography } from '@mui/material'
import PortfolioHeatStress from '../components/climateAnalytics/portfolio/PortfolioHeatStress'
import PortfolioExtremeWind from '../components/climateAnalytics/portfolio/PortfolioExtremeWind'
import PortfolioFlood from '../components/climateAnalytics/portfolio/PortfolioFlood'
import PortfolioWaterRisk from '../components/climateAnalytics/portfolio/PortfolioWaterRisk'
import PortfolioWildfire from '../components/climateAnalytics/portfolio/PortfolioWildfire'
import siteMap from '../siteMap'
import { useClimateHazards } from '../hooks/analytics'
import { CanonicalPeriod, EmissionTier } from '../components/climateAnalytics/common'
import EmissionTierSelector from '../components/climateAnalytics/common/EmissionTierSelector'
import HorizonSelector from '../components/climateAnalytics/common/HorizonSelector'
import HazardSelector from '../components/climateAnalytics/common/HazardSelector'
import ClimateContextBar from '../components/climateAnalytics/common/ClimateContextBar'
import HazardScenarioPreferenceControl from '../components/climateAnalytics/common/HazardScenarioPreferenceControl'
import { useHazardScenarioPreferences } from '../components/climateAnalytics/common/useHazardScenarioPreferences'
import { useClimateAnalyticsRouteState } from '../components/climateAnalytics/common/useClimateAnalyticsRouteState'
import { ClimateAnalyticsPermissions, PermissionGate } from 'balkerne-core/roles'
import { RootState } from '../store'

const ClimateAnalytics = () => {
  const history = useHistory()
  const location = useLocation()
  const hazardMatch = useRouteMatch<{ hazard?: string }>(`${siteMap.ClimateAnalytics.path}/:hazard`)
  const theme = useTheme()
  const authUser = useSelector((state: RootState) => state.auth.user)
  const { data: hazards, isLoading: isHazardsLoading } = useClimateHazards()

  const navigation = useMemo(
    () => ({
      push: (path: string) => history.push(path),
      replace: (path: string) => history.replace(path),
    }),
    [history],
  )
  const buildClimateAnalyticsPath = useCallback(
    (hazardCode: string, nextEmissionTier: EmissionTier, nextPeriod: CanonicalPeriod) =>
      siteMap.ClimateAnalytics.getPath(hazardCode, nextEmissionTier, nextPeriod),
    [],
  )
  const {
    emissionTier,
    handleEmissionTierChange,
    handleHazardChange,
    handlePeriodChange,
    hazardViewConfig,
    selectedHazardCode,
    selectedPeriod,
  } = useClimateAnalyticsRouteState({
    hazards,
    hazardFromRoute: hazardMatch?.params?.hazard,
    pathname: location.pathname,
    search: location.search,
    navigation,
    buildPath: buildClimateAnalyticsPath,
  })
  const hazardScenarioPreferences = useHazardScenarioPreferences(
    authUser,
    selectedHazardCode,
    hazardViewConfig.scenarioPreferences,
  )

  const hazard = useMemo(() => {
    return hazards.find(h => h.name === selectedHazardCode)
  }, [hazards, selectedHazardCode])

  const renderContent = useMemo(() => {
    switch (selectedHazardCode) {
      case 'fld':
        return (
          <PortfolioFlood
            emissionTier={emissionTier}
            selectedPeriod={selectedPeriod}
            filters={hazardScenarioPreferences.filters}
          />
        )
      case 'hs':
        return <PortfolioHeatStress emissionTier={emissionTier} selectedPeriod={selectedPeriod} />
      case 'tc':
        return <PortfolioExtremeWind emissionTier={emissionTier} selectedPeriod={selectedPeriod} />
      case 'wr':
        return <PortfolioWaterRisk emissionTier={emissionTier} selectedPeriod={selectedPeriod} />
      case 'wf':
        return <PortfolioWildfire emissionTier={emissionTier} selectedPeriod={selectedPeriod} />
      default:
        return null
    }
  }, [emissionTier, hazardScenarioPreferences.filters, selectedHazardCode, selectedPeriod])

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar + 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'saturate(180%) blur(6px)',
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 } }}>
          {/* Eyebrow + Primary actions (row 1) */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
            py={1.5}
          >
            <Typography variant="overline" color="text.secondary">
              Climate Analytics
            </Typography>

            <Stack direction="row" spacing={1}>
              <PermissionGate action={ClimateAnalyticsPermissions.Modify}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => history.push(siteMap.ClimateAnalyticsConfigure.path)}
                >
                  Configure Vulnerability
                </Button>
              </PermissionGate>
              <Button
                size="small"
                variant="contained"
                onClick={() => history.push(siteMap.ClimateAnalyticsExport.path)}
              >
                Export Data
              </Button>
            </Stack>
          </Stack>

          <Stack spacing={1.5} pb={2}>
            <Box sx={{ minWidth: 0, maxWidth: 760 }}>
              <Typography variant="h4" fontWeight={800}>
                {hazard ? hazard.display_name : 'Hazard'}
              </Typography>
              {hazard?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  {hazard.description}
                </Typography>
              )}
            </Box>

            <ClimateContextBar
              hazardControl={
                <HazardSelector
                  hazards={hazards}
                  value={selectedHazardCode ?? ''}
                  onChange={handleHazardChange}
                  selectedAction={
                    hazardViewConfig.scenarioPreferences ? (
                      <HazardScenarioPreferenceControl
                        filters={hazardScenarioPreferences.filters}
                        hazardCode={selectedHazardCode}
                        hazardLabel={hazard?.display_name ?? 'Hazard'}
                        config={hazardViewConfig.scenarioPreferences}
                        onSave={hazardScenarioPreferences.saveFilters}
                      />
                    ) : undefined
                  }
                />
              }
              emissionControl={<EmissionTierSelector value={emissionTier} onChange={handleEmissionTierChange} />}
              horizonControl={
                <HorizonSelector
                  value={selectedPeriod}
                  availablePeriods={hazardViewConfig.availablePeriods}
                  onChange={handlePeriodChange}
                />
              }
            />
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      {isHazardsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
          {renderContent}
        </Container>
      )}
    </Box>
  )
}

export default ClimateAnalytics
